import { useEffect, useState } from 'react';
import { Form, ResourceContextProvider, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import { Box, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslate } from 'ra-core';
import authProvider from '../../../utils/authProvider';
import { typedHttpClient } from '../../../utils/httpClient';
import {
  AppConfigConfigType,
  AppConfigDefinitionType,
} from '../../../lib/constants/typedApiReturnTypes';
import { ClientConfigFormGenerator } from '../../../lib/clientConfigFormGenerator';
import {
  saveClientConfigDefaults,
} from '../../../lib/clientConfigHelpers';

export const AppConfigDefaults = () => {

  const [definition, setDefinition] = useState<AppConfigDefinitionType | null>(null);
  const [config, setConfig] = useState<AppConfigConfigType | undefined>();
  const [updateTimestamp, setUpdateTimestamp] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const isDevAdmin = authProvider.isDeveloperAdmin();

  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    typedHttpClient().clientConfig.clientConfigControllerResolveAppDefaultConfigV0().then((response) => {

      setDefinition(response.data.definition ?? null);
      setConfig(response.data.config);

    });

  }, [updateTimestamp]);

  const updateConfig = async (values: FieldValues) => {

    if (!definition) {

      return;

    }

    const hasError = await saveClientConfigDefaults(
      'app',
      0,
      definition,
      config as Record<string, unknown> | undefined,
      values as Record<string, unknown>,
    );
    if (hasError) {

      notify('resources.config.text.defaults_update_error', { type: 'error' });

    } else {

      notify('resources.config.text.defaults_updated');

    }
    setUpdateTimestamp(Date.now());

  };

  return (
    <div>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}>
        <h1 style={{ margin: 0 }}>{translate('menu.items.app-defaults')}</h1>
        <TextField
          size="small"
          placeholder={translate('ra.action.search')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <ResourceContextProvider value={'ClientConfig'}>
        {/* noValidate: collapsed groups use display:none; browser HTML5 validation cannot focus required hidden inputs */}
        <Form noValidate reValidateMode={'onChange'} record={config} mode="onBlur" onSubmit={updateConfig} sanitizeEmptyValues>
          {definition && <ClientConfigFormGenerator definitions={definition} searchQuery={searchTerm} />}
        </Form>
      </ResourceContextProvider>
    </div>
  );

};
